/**
 * send-to-kindle-launch.js
 * @author: Bernhard Wolkerstorfer
 *
 * Description: Launch logic for Send-to-Kindle.
 *
 * Copyright (c) 2012 Amazon.com, Inc. All rights reserved.
 */
import {getState} from "./state/state";
import {StatusMessage} from "./status/status-message";
import {createExtractor} from "./extractor/extractor-mapping";
import {History} from "./history/history-overlay";

import {EXTRACTION_TIMEOUT_MILLIS, NETWORK_STATUS_CODE} from "../src-common/constants";
import {MetricsNames} from "./metrics/metrics";
import {addUnloadListener, preventFromUnload, removeUnloadListener} from "../src-common/utils/s2k-before-unload";
import {S2KWebExtractor} from "./extractor/s2k-web-extractor";

// #################################################################### 
// ## Initialization Logic                                            ##
// ####################################################################
const initializeContentScript = (injectionContext, s2kGuid, extensionId, lowQualityAction) => {
    getState().initialize(injectionContext, s2kGuid, extensionId, lowQualityAction);
    injectBasicCSS(extensionId);
};

/**
 * Inject basic css to current page
 */
const injectBasicCSS = (extensionId) => {
    let cssRotate = `
      @keyframes rotate360 {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `;
    let cssFont = `
        @font-face {
            font-family: 'Amazon Ember';
            src: url("_EXTURL_/fonts/AmazonEmber_Rg.ttf");
            font-weight: normal;
            font-style: normal;
            font-display: swap; 
        }
        @font-face {
            font-family: 'Amazon Ember';
            src: url("_EXTURL_/fonts/AmazonEmber_Bd.ttf");
            font-weight: bold;
            font-style: normal;
        }
    `;
    // Inject animation and Font
    const styleElement = document.createElement('style');
    let extensionUrl = `chrome-extension://${extensionId}`;
    cssFont = cssFont.replaceAll("_EXTURL_", extensionUrl);
    // Add styles into head of current page
    styleElement.textContent = cssRotate + cssFont;
    window.document.head.append(styleElement);
};


// #################################################################### 
// ## Extraction Logic                                               ##
// ####################################################################
/**
 * Method to handle extraction progress messages.
 * @param {Object} status Status
 */
const launchProgress = function (status) {
    // Stop monitoring unload action
    removeUnloadListener(preventFromUnload);
    // Extraction finished, record time
    chrome.runtime.sendMessage({
        action: "metrics-stop-timer",
        data: {
            metricsName: MetricsNames["extract-time"]
        }
    });
    const state = getState();
    let task = this;
    // This domain is used for reporting domain as metrics
    let domain = new URL(state.stateDocument.URL || "").hostname || "";
    // Handle the extraction result.
    if (status.success === true && state.cancelProgress === false) {
        // record metrics
        chrome.runtime.sendMessage({
            action: "metrics-add-counter",
            data: {
                metricsName: MetricsNames["extract-success"]
            }
        });
        // Copy extraction result.
        if (!state.content) {
            state.content = status.data;
        }

        // Handle low quality results based on customer preferences, if the customer
        // has nothing selected or selected to send anyway ignore the LQ flag. This
        // logic only applies to "Send to Kindle" cases.
        status.lowQuality = (state.isPreviewOnly === false && status.lowQuality === true);
        if (status.lowQuality === true && state.lowQualityAction === "ASK") {
            // Low quality message and wait for user action.
            state.statusMessage.showAlert("result", "lowQualityAlert");

            // Reset in progress
            state.resetProgress();
            return;
        } else if (status.lowQuality === true && state.lowQualityAction === "PREVIEW") {
            // Customer wants to preview in low quality cases.
            state.isPreviewOnly = true;
        }

        // Redirect customer to preview.
        if (state.isPreviewOnly === true && status.plugin !== true) {
            // Report domain
            chrome.runtime.sendMessage({
                action: "report-domain",
                data: {
                    operation: "preview",
                    domain: domain
                }
            });
            redirectToPreview();

            // Reset in progress
            state.resetProgress();
        } else if (state.isPreviewOnly === false) {
            // Prevent from unload page
            addUnloadListener(preventFromUnload);
            // Report domain
            chrome.runtime.sendMessage({
                action: "report-domain",
                data: {
                    operation: "send",
                    domain: domain
                }
            });
            // Start send progress
            chrome.runtime.sendMessage({
                    action: "store-content",
                    data: {
                        "content": state.content.asStorageJson(),
                        "isFromSend": true
                    }
                }, function ({token}) {
                    // Show send in progress alert
                    state.statusMessage.showAlert("progress", "sendingToKindle");
                    chrome.runtime.sendMessage({
                        action: "send-document",
                        data: {token: token, context: "OCS"}
                    }, function (res) {
                        // Stop monitoring unload action
                        removeUnloadListener(preventFromUnload);
                        if (res.statusCode === NETWORK_STATUS_CODE.NO_NETWORK) {
                            // Show network alert
                            state.statusMessage.showAlert("result", "networkError");
                        } else if (res.success && res.statusCode !== NETWORK_STATUS_CODE.CANCEL) {
                            // Show success alert if send failed
                            state.statusMessage.showAlert("result", "sending");
                        } else if (res.statusCode !== NETWORK_STATUS_CODE.CANCEL) {
                            // Show error alert if send failed
                            state.statusMessage.showAlert("result", "generalError");
                        }

                        // Reset in progress
                        state.resetProgress();
                    });
                });
        } else {
            handleExtractionFailed(false);
        }
    } else if ((status.error === true && state.cancelProgress === false) || task.isTimedOut()) {
        handleExtractionFailed(task.isTimedOut());
    } else {
        handleExtractionFailed(false);
    }
};

/**
 * Handler for actions after extraction failed
 * Need to record Domain, Url and reset progress
 * @param {boolean} isTimeout Whether the failed reason is timeout
 * @param {string} domain The domain of webpage extracted
 */
const handleExtractionFailed = (isTimeout, domain) => {
    const state = getState();
    // If the reason is timeout, rating is -2, otherwise rating is -1
    let rating = isTimeout ? -2 : -1;
    let metricsName = isTimeout ? MetricsNames["extract-timeout"] : MetricsNames["extract-error"];

    // Record Url and Domain
    chrome.runtime.sendMessage({
        action: "report-low-rating",
        data: {
            rating: rating,
            url: state.stateDocument.URL || ""
        }
    });
    chrome.runtime.sendMessage({
        action: "report-domain",
        data: {
            operation: "error",
            domain: domain
        }
    });
    // record metrics
    chrome.runtime.sendMessage({
        action: "metrics-add-counter",
        data: {
            metricsName: metricsName
        }
    });
    // Show extraction error.
    state.statusMessage.showAlert("result", "extractionError");
    // Reset progress state
    state.resetProgress();
};

/**
 * Handler for when the service worker needs to update the low quality action
 * @param {string} lowQualityAction
 */
const updateLowQualityAction = (lowQualityAction) => {
    const state = getState();
    state.lowQualityAction = lowQualityAction;
};

/**
 * Entry point for extraction engine.
 * @param {boolean} previewOnly   Preview and Send
 * @param {boolean} selectedOnly  Selected text only.
 * @param {boolean} usedShortcut  Engine was triggered by shortcut.
 */
const launchExtraction = function (previewOnly, selectedOnly, usedShortcut) {
    // Load extension state for current window.
    const state = getState();

    if (state.inProgress) {
        return;
    }

    // Somehow, if we're trying to extract content before the
    // content script has been fully injected and the state transition
    // between the SW and the content script has finished, try again in 250ms
    if (!state.isInjected) {
        setTimeout(launchExtraction, 250);
        return;
    }

    // Set desired action in state.
    state.isPreviewOnly = previewOnly || false;
    state.isSelectedOnly = selectedOnly || false;
    state.isShortcut = usedShortcut || false;

    // Initialize progress information.
    state.statusMessage = state.statusMessage || new StatusMessage("status");
    // Reset progress state
    state.resetProgress();
    // Register cancel event
    state.statusMessage.registerEvent("cancelProgress", function () {
        // Stop monitoring unload action
        removeUnloadListener(preventFromUnload);
        state.cancelProgress = true;
        chrome.runtime.sendMessage({
            action: "set-cancel-sending",
            data: {
                cancel: true
            }
        });
    });

    // Perform action only, if the content has a body and it is not already a preview.    
    if (state.stateDocument.body && !state.stateDocument.location.pathname.match(/^\/gp\/sendtokindle\/(reader|setup)/i)) {
        // Record metrics
        chrome.runtime.sendMessage({
            action: "metrics-add-counter",
            data: {
                metricsName: MetricsNames["extract-count"]
            }
        });
        chrome.runtime.sendMessage({
            action: "metrics-start-timer",
            data: {
                metricsName: MetricsNames["extract-time"]
            }
        });
        // Use `report-low-rating` metrics to record all url when starting extraction, rating as "999"
        chrome.runtime.sendMessage({
            action: "report-low-rating",
            data: {
                rating: "999",
                url: state.stateDocument.URL || ""
            }
        });
        // Prevent from unload page
        addUnloadListener(preventFromUnload);
        // Show analyzing alert
        state.statusMessage.showAlert("progress", "analyzing");
        // Release UI thread for drawing the spinner.
        setTimeout(function () {
            // Run extraction process, if necessarily.
            if (!state.content) {
                // New logic (call web-extraction) will be executed first
                let newExtractor = new S2KWebExtractor();
                let task = {
                    timeoutTimeInMillis: Date.now() + EXTRACTION_TIMEOUT_MILLIS,
                    isTimedOut: function () {
                        return Date.now() > this.timeoutTimeInMillis;
                    }
                };
                task.progress = launchProgress.bind(task);
                // This `backup` is old logic, it's a backup logic for now. If the web-extraction failed, will trigger this
                task.backup = function () {
                    let extractor = createExtractor(state);
                    extractor.extract(task);
                };
                // Execute new extract logic
                newExtractor.extract(task);
            } else {
                launchProgress({"success": true, "data": state.content, "value": 1.0});
            }
        }, 0);
    }
};

//#################################################################### 
// ## Preview Logic                                                 ##
//####################################################################

/**
 * Redirect current page to Amazon S2K Viewer.
 */
const redirectToPreview = function () {
    // Prevent from unload page
    addUnloadListener(preventFromUnload);
    const state = getState();
    if (state && state.content) {
        // show preview in progress alert
        state.statusMessage.showAlert("progress", "loadingPreview");

        // Request the browser to open the preview.
        chrome.runtime.sendMessage(
            {
                action: "store-content",
                data: {
                    "content": state.content.asStorageJson()
                }
            }, function (response) {
                // Stop monitoring unload action
                removeUnloadListener(preventFromUnload);
                // Redirect to preview.
                let previewUrl = response.url + "&isSelection=" + (state.isSelectedOnly ? "true" : "false");
                window.location.assign(previewUrl);
            });
    }
};

//#################################################################### 
// ## History Logic                                                 ##
//####################################################################
const launchHistory = () => {
    const state = getState();
    state.initialize("history");

    if (!state.history) {
        state.history = new History();
    }

    state.history.inject();
};


// These are the entry-points for code executed via the service worker
globalThis.updateLowQualityAction = updateLowQualityAction;
globalThis.launchExtraction = launchExtraction;
globalThis.launchHistory = launchHistory;
globalThis.initializeContentScript = initializeContentScript;
